<template>
  <div class="job-detail f-c" v-loading="loadingJobInfo">
    <job-detail-stats :jobId="jobId" :job="job" v-if="!loadingJobInfo" />
    <div class="features-container">
      <div class="f-r-vc detail-page-basic-info" style="gap: 20px;">
        <span class="entity-no">{{ jobNo }}</span>
        <el-tag effect="plain" class="basic-info-tag">{{
          jobInfo.type
        }}</el-tag>
        <el-tag effect="plain" class="basic-info-tag">
          {{ jobInfo.status }}
        </el-tag>
        <el-progress
          v-if="
            [JobTypes.InboundJob, JobTypes.OutboundJob].includes(jobType) &&
            jobStatus != JobStatus.JobDeleted
          "
          :percentage="progress"
          :stroke-width="26"
          :format="format"
        />
      </div>
      <div class="features f-r-vc">
        <el-button
          type="primary"
          class="feature-secondary"
          @click="goToOrderDetail(jobOrder.no)"
          v-if="orderId"
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px;"
          />
          {{ riiOrderNo }}
        </el-button>
        <el-button
          type="primary"
          class="feature-secondary"
          @click="goToOrderDetail(jobOrder.no)"
          v-if="orderId"
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px;"
          />
          {{ riiContainerNo }}
        </el-button>
        <el-button
          type="success"
          class="feature-secondary"
          @click="goToJobDetail(job.unloadingJobNo)"
          v-if="jobType === JobTypes.InboundJob && job.unloadingJobId"
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px;"
          />
          Unloading Job
        </el-button>
        <el-button
          type="success"
          class="feature-secondary"
          @click="downloadJobInstruction"
          v-if="
            jobType === JobTypes.UnloadingJob &&
            [JobStatus.JobProcessing, JobStatus.JobCompleted].includes(
              jobStatus
            )
          "
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px;"
          />
          Warehouse Receipt
        </el-button>
        <el-button
          type="success"
          class="feature-secondary"
          @click="labelJob"
          v-if="
            jobType === JobTypes.UnloadingJob &&
            [JobStatus.JobProcessing, JobStatus.JobCompleted].includes(
              jobStatus
            )
          "
        >
          <svg-icon icon-class="labels" class="svg-white-color" />
          Job Labels
        </el-button>
        <el-button
          type="primary"
          class="feature-secondary"
          @click="goToShipmentDetail(jobInfo.shipmentNo)"
          v-if="jobInfo.shipmentNo"
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px;"
          />
          Shipment Detail
        </el-button>
        <el-button
          type="warning"
          @click="openVerificationDialog"
          class="feature-secondary"
          v-if="
            jobType === JobTypes.UnloadingJob &&
            jobStatus === JobStatus.JobProcessing
          "
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px;"
          />
          Complete Job
        </el-button>
        <el-button
          type="danger"
          class="feature-secondary"
          @click="goToEditJob({ jobNo })"
          v-if="canEditJob"
        >
          <svg-icon
            icon-class="pencil"
            class="svg-white-color"
            style="font-size: 15px;"
          />
          Edit Job
        </el-button>
        <el-button
          type="warning"
          class="feature-secondary"
          @click="setJobStatusProcessing"
          v-if="
            jobType === JobTypes.UnloadingJob &&
            jobStatus === JobStatus.JobAssigned
          "
        >
          <svg-icon
            icon-class="pencil"
            class="svg-white-color"
            style="font-size: 15px;"
          />
          Start Unloading
        </el-button>
        <el-button
          type="warning"
          class="feature-secondary"
          @click="openAssignJob"
          v-if="
            jobType !== JobTypes.UnloadingJob &&
            [
              JobStatus.JobAssigned,
              JobStatus.JobUnassigned,
              JobStatus.JobProcessing
            ].includes(jobStatus)
          "
        >
          <svg-icon icon-class="pencil" class="svg-white-color" />
          Assign Job
        </el-button>
        <el-button
          type="danger"
          class="feature-secondary"
          @click="deleteJob"
          v-if="
            ![JobStatus.JobDeleted, JobStatus.JobCompleted].includes(jobStatus)
          "
        >
          <svg-icon
            icon-class="trash"
            class="svg-white-color"
            style="font-size: 14px;"
          />
          Delete Job
        </el-button>
      </div>
    </div>
    <div class="f-r job-info-container">
      <job-info :job-id="jobId" :job="jobInfo" :editable="false" />
      <common-card class="job-qr" title="QR Code">
        <img :src="jobQrSrc" />
      </common-card>
    </div>
    <container-info
      v-if="jobType === UnloadingJob"
      :order-status="orderStatus"
      :container-id="containerId"
      :container="container"
      :enableEdit="false"
    />
    <div class="tabs">
      <el-tabs v-model="selectedTab" @tab-click="selectTabChange">
        <el-tab-pane
          label="Instruction"
          :name="JobContentTab"
          v-if="jobType === UnloadingJob"
        >
          <job-content
            :job-content="goodsGroup"
            :job-id="jobId"
            :order-id="orderId"
            :job-status="jobStatus"
            :show-pallet-list="true"
          />
        </el-tab-pane>
        <el-tab-pane label="Pallets" :name="PalletListTab">
          <pallet-list
            :palletQuery="palletIds.length > 0 ? { ids: palletIds } : null"
            v-if="palletIds.length > 0"
            :actionWidth="140"
            :showGroupName="jobType === UnloadingJob"
          >
            <template v-slot:actions="props">
              <div class="actions f-r" style="margin-left: 10px;">
                <el-popover
                  trigger="hover"
                  placement="top-start"
                  @mouseenter.native="getJobQrCode(props.row, props.index)"
                >
                  <img :src="srcList[props.index]" />
                  <el-button size="small" type="text" slot="reference">
                    QR Code
                  </el-button>
                </el-popover>
              </div>
            </template>
          </pallet-list>
        </el-tab-pane>
        <el-tab-pane label="Notes" :name="NotesTab">
          <el-button
            size="small"
            plain
            type="primary"
            @click="newJobNote"
            :disabled="jobStatus === JobDeleted"
          >
            Add New Note
          </el-button>
          <note-list
            :notes="notes"
            :change-cb="modifyJobNotes"
            :disabled="
              [JobStatus.JobCompleted, JobStatus.JobDeleted].includes(
                job.status
              )
            "
          />
        </el-tab-pane>
        <el-tab-pane
          label="Inbound Jobs"
          :name="InboundJobsTab"
          v-if="jobType === JobTypes.UnloadingJob"
        >
          <job-list
            :jobQuery="{ unloadingJobId: jobId }"
            :tableHeaders="UnloadingJobTableHeaders"
          />
        </el-tab-pane>
      </el-tabs>
    </div>
    <note-edit ref="newNote" />
    <common-dialog
      ref="assignJobDialog"
      title="Assign Job"
      :inputs-map="assignJobForm"
      :save-cb="assignJob"
    />
    <unloading-job-verification-dialog
      ref="verificationDialog"
      :job-id="jobId"
      :groups="groups"
    />
  </div>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep'
import { FormModes } from '@/core/configs/types'
// models
import { AssignJobForm, UnloadingJobTableHeaders } from '@/core/models/job'
import {
  JobTypes,
  JobDeleted,
  JobStatus,
  JobProcessing,
  UnloadingJob,
  InboundJob,
  OutboundJob,
  DivideJob
} from '@/core/models/constant'
// apis
import {
  detailJob,
  removeJob,
  changeJobAssignee,
  modifyJob,
  modifyJobNotes,
  labelJobPallets,
  unloadingInstruction
} from '@/api/job'
import { viewPdf } from '@/core/utils'
// mixin
import { pageJumps } from '@/core/mixin/pages'
import { TabsMixin } from '@/core/mixin/tabs'
// components
import JobInfo from '@/components/jobs/JobInfo.vue'
import JobContent from '@/components/jobs/JobContent.vue'
import PalletList from '@/components/pallet/PalletList.vue'
import NoteList from '@/components/note/NoteList.vue'
import NoteEdit from '@/components/note/NoteNew.vue'
import CommonDialog from '@/components/common/CommonDialog.vue'
import UnloadingJobVerificationDialog from '@/components/jobs/UnloadingJobVerificationDialog.vue'
import JobList from '@/components/jobs/JobList.vue'
import CommonCard from '@/components/common/CommonCard.vue'
import JobDetailStats from '@/components/jobs/JobDetailStats.vue'
import ContainerInfo from '@/components/container/ContainerInfo.vue'
// msg
import PagesMessage from '@/core/message/pages'
import { detailOrder } from '@/api/order'
import { queryAddress } from '@/api/address'

const QRCode = require('qrcode')

export default {
  name: 'JobDetail',
  mixins: [pageJumps, TabsMixin],
  components: {
    JobInfo,
    JobContent,
    PalletList,
    NoteList,
    NoteEdit,
    CommonDialog,
    UnloadingJobVerificationDialog,
    JobList,
    CommonCard,
    JobDetailStats,
    ContainerInfo
  },
  data() {
    return {
      UnloadingJobTableHeaders,
      FormModes,
      JobTypes,
      JobDeleted,
      UnloadingJob,
      assignJobForm: cloneDeep(AssignJobForm),
      loadingJobInfo: false,
      job: {},
      groups: [],
      srcList: [],
      JobStatus,
      selectedInboundJobId: null,
      jobOrder: null,
      jobQrSrc: ''
    }
  },
  computed: {
    orderId() {
      return this.job.orderId
    },
    orderNo() {
      return this.jobOrder?.no ?? ''
    },
    riiOrderNo() {
      return this.jobOrder?.riiOrderNo ?? ''
    },
    orderStatus() {
      return this.jobOrder?.status
    },
    jobId() {
      return this.job?.id ?? ''
    },
    jobNo() {
      return this.$route.params.no
    },
    jobType() {
      return this.job.type ?? ''
    },
    jobStatus() {
      return this.job?.status ?? ''
    },
    jobInfo() {
      return this.job
    },
    palletIds() {
      const procesing = this.job?.processingPalletIds ?? []
      const done = this.job?.donePalletIds ?? []
      return [...procesing, ...done]
    },
    goodsGroup() {
      return this.job?.goodsGroups ?? []
    },
    notes() {
      return this.job?.notes ?? []
    },
    Tabs() {
      switch (this.jobType) {
        case UnloadingJob:
          return [
            this.JobContentTab,
            this.PalletListTab,
            this.NotesTab,
            this.InboundJobsTab
          ]
        case InboundJob:
        case OutboundJob:
        case DivideJob:
          return [this.PalletListTab, this.NotesTab]
        default:
          return [this.PalletListTab, this.NotesTab]
      }
    },
    completedPalletCount() {
      return this.job?.donePalletIds.length ?? 0
    },
    totalPalletCount() {
      if (this.job.type === JobTypes.InboundJob && this.job.unloadingJobId) {
        return this.job?.actualPalletCount ?? 0
      } else {
        return (
          (this.job?.donePalletIds.length ?? 0) +
          (this.job?.processingPalletIds.length ?? 0)
        )
      }
    },
    selectedTab: {
      get() {
        return this.$route.query?.tab
      },
      set(value) {
        if (this.Tabs.includes(value)) {
          this.goToJobDetail(this.jobNo, { tab: value })
        } else {
          let tab
          switch (this.jobType) {
            case UnloadingJob:
              tab = this.JobContentTab
              break
            default:
              tab = this.PalletListTab
          }
          this.goToJobDetail(this.jobNo, { tab }, true)
        }
      }
    },
    progress() {
      if (this.totalPalletCount > 0) {
        return (this.completedPalletCount / this.totalPalletCount) * 100
      }
      return 0
    },
    container() {
      return this.jobOrder?.container ?? null
    },
    containerNo() {
      return this.job?.containerNo ?? ''
    },
    containerId() {
      return this.job?.containerId ?? ''
    },
    riiContainerNo() {
      return this.jobOrder?.container.no ?? ''
    },
    canEditJob() {
      const flag1 = ![
        JobStatus.JobProcessing,
        JobStatus.JobCompleted,
        JobStatus.JobDeleted
      ].includes(this.jobStatus)
      const flag2 =
        (this.job.unloadingJobId ?? null) === null &&
        (this.job.shipmentId ?? null) === null
      const flag3 = this.jobType !== JobTypes.OutboundJob
      return flag1 && flag2 && flag3
    },
    statusTagType() {
      return this.jobStatus === JobStatus.JobDeleted ? 'danger' : null
    }
  },
  mounted() {
    this.getJobDetail()
    PagesMessage.on(PagesMessage.eventMap.REFRESH_JOB_DETAIL, this.getJobDetail)
  },
  beforeDestroy() {
    PagesMessage.off(
      PagesMessage.eventMap.REFRESH_JOB_DETAIL,
      this.getJobDetail
    )
  },
  methods: {
    // -------------------------- job apis --------------------------
    newJobNote() {
      const saveNote = (newNote, done, error) => {
        modifyJobNotes(this.jobId, [...this.notes, newNote])
          .then(() => {
            done()
            this.$message({
              type: 'success',
              message: 'Successfully add a new job note!'
            })
            PagesMessage.emit(PagesMessage.eventMap.REFRESH_JOB_DETAIL)
          })
          .catch(err => {
            error(err)
          })
      }
      this.$refs.newNote.open(saveNote)
    },
    async getJobDetail() {
      this.loadingJobInfo = true
      const res = await detailJob(this.jobNo)
      this.getJobOrder(res.orderId)
      // get address for each group
      const addressIds = res.goodsGroups.map(one => one.addressId)
      const addressPage = await queryAddress({ addressIds, size: -1 })
      const addressMap = Object.fromEntries(
        addressPage.records.map(one => [one.id, one])
      )
      res.goodsGroups.forEach(one => {
        one.address = addressMap[one.addressId]
      })
      this.$set(this, 'job', res)
      this.loadingJobInfo = false
      this.getJobQrSrc()
    },
    async getJobOrder(orderId) {
      const order = await detailOrder(orderId)
      this.jobOrder = order
    },
    deleteJob() {
      this.$confirm('Delete job. Continue?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(() => {
          removeJob(this.jobId).then(res => {
            if (res) {
              PagesMessage.emit(PagesMessage.eventMap.REFRESH_JOB_DETAIL)
            } else {
              this.$message.error('Delete job failed!')
            }
          })
        })
        .catch(() => {})
    },
    openVerificationDialog() {
      this.$refs.verificationDialog.open()
    },
    modifyJobNotes(notes) {
      modifyJob({
        id: this.jobId,
        notes
      }).then(res => {
        if (res) {
          this.getJobDetail()
        } else {
          this.$message.error('Update job notes failed!')
        }
      })
    },
    selectTabChange() {},
    async getJobQrCode(pallet, index) {
      const src = await QRCode.toDataURL(
        JSON.stringify({
          type: 'Pallet',
          id: pallet.id,
          no: pallet.no
        }),
        {
          margin: 0,
          width: 170,
          height: 170
        }
      )
      this.$set(this.srcList, index, src)
    },
    format() {
      return `${this.completedPalletCount} / ${this.totalPalletCount}`
    },
    setJobStatusProcessing() {
      this.$confirm(
        'Start Unloading job and print job instruction. Continue?',
        'Warning',
        {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        modifyJob({ id: this.jobId, status: JobProcessing }).then(res => {
          if (res) PagesMessage.emit(PagesMessage.eventMap.REFRESH_JOB_DETAIL)
        })
      })
    },
    openAssignJob() {
      this.assignJobForm[0].value = this.job.no
      this.assignJobForm[1].value = this.job.assigneeId
      this.$refs.assignJobDialog.open()
    },
    assignJob(res) {
      changeJobAssignee(this.jobId, res[1].value).then(res => {
        if (res) {
          PagesMessage.emit(PagesMessage.eventMap.REFRESH_JOB_DETAIL)
        } else {
          this.$message.error('Failed to change job assigee!')
        }
      })
      return [true, '']
    },
    async getJobQrSrc() {
      const src = await QRCode.toDataURL(
        JSON.stringify({
          type: 'Job',
          id: this.jobId,
          no: this.jobNo
        }),
        {
          margin: 0
        }
      )
      this.jobQrSrc = src
    },
    openQrCode() {
      this.getJobQrSrc().then(e => this.$refs.qrCode.open())
    },
    async labelJob() {
      const blob = await labelJobPallets(this.jobId)
      viewPdf(blob)
    },
    async downloadJobInstruction() {
      const blob = await unloadingInstruction(this.jobId)
      viewPdf(blob)
    }
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.job-detail {
  gap: 20px;

  @extend .common-card-margin;

  .features-container {
    @extend .f-c;

    gap: 20px;
    justify-content: space-between;

    .detail-page-basic-info {
      .el-progress {
        width: 500px;
        margin: 0 !important;

        @extend .f-r;

        align-items: center;

        ::v-deep .el-progress__text {
          white-space: nowrap;
          color: black;
          font-size: 48px !important;
          font-weight: bolder;
          margin-left: 20px;
          font-family: 'oswald bold', sans-serif;
        }

        ::v-deep .el-progress-bar {
          width: 500px;

          .el-progress-bar__outer {
            height: 36px !important;

            @extend .shadow-level-one;
          }
        }
      }

      &::v-deep .el-tag {
        font-family: 'oswald bold', sans-serif;
        color: white;
        font-size: 24px;
        min-height: 50px;
        border-width: 0;
        background-color: $business-color;

        @extend .f-r-c-c;
        @extend .shadow-level-three;
      }
    }
  }

  .placeholder {
    height: 78px;
  }

  .tabs {
    border-radius: 4px;
    padding: 10px 20px;
    background: white;

    @extend .shadow-level-three;
  }

  &::v-deep .common-card {
    margin: 0 !important;
  }
}

.actions {
  flex-wrap: wrap;
  justify-content: center;
  gap: 5px 10px;

  .el-button {
    margin: 0 !important;
  }
}

.job-info-container {
  gap: 20px;

  .job-info {
    flex-grow: 1;
  }

  .job-qr {
    width: 120px;

    img {
      height: 120px;
    }
  }
}
</style>
